// ClusterManager.js
import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, Chip, Stack, TextField, IconButton, Button, DialogActions, Slider, Switch } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import GitHubIcon from '@mui/icons-material/GitHub';
import { DATA_URL, getGeojsonFile, RESERVATIONS_URL } from '../config';

const ClusterManager = ({ showClusterManager, closeClusterManager, selectedLocation, userData }) => {
  const [housekeepers, setHousekeepers] = useState([]);
  const [newHousekeeperCID, setNewHousekeeperCID] = useState('');
  const [reportCID, setReportCID] = useState('');
  const [reportExplanation, setReportExplanation] = useState('');
  const [maxReservableByOne, setMaxReservableByOne] = useState(4);
  const [maxReservations, setMaxReservations] = useState(10);
  const [maxAreasInOne, setMaxAreasInOne] = useState(25);
  const [forceVerticalLimits, setForceVerticalLimits] = useState(false);
  const [clusterIsActive, setClusterIsActive] = useState(false);
  const [pastReservations, setPastReservations] = useState(0);
  const [notifications, setNotifications] = useState(false);
  const [areaCount, setAreaCount] = useState(0);
  const [currentReservations, setCurrentReservations] = useState(0);
  const [colorTheme, setColorTheme] = useState('#1a475f');
  const [aresUserText, setAresUserText] = useState(false);

  useEffect(() => {
    const fetchHousekeepers = async () => {
      try {
        const response = await fetch(DATA_URL + `housekeepers/${encodeURIComponent(selectedLocation)}?t=${Date.now()}`, {
          credentials: 'include',
          cache: 'no-store'
        });
        if (response.ok) {
          const data = await response.json();
          setHousekeepers(data);
        } else {
          console.error('Failed to fetch housekeepers:', response.status);
          setHousekeepers([]);
        }
      } catch (error) {
        console.error('Error fetching housekeepers:', error);
        setHousekeepers([]);
      }
    };
    fetchHousekeepers();
  }, [selectedLocation]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(DATA_URL + `settings/${encodeURIComponent(selectedLocation)}?t=${Date.now()}`, {
          credentials: 'include',
          cache: 'no-store'
        });
        if (response.ok) {
          const data = await response.json();
          setMaxReservableByOne(data.maxReservableByOne ?? 0);
          setMaxReservations(data.maxReservations ?? 0);
          setMaxAreasInOne(data.maxAreasInOne ?? 0);
          setForceVerticalLimits(!!data.forceVerticalLimits);
          setClusterIsActive(!!data.clusterIsActive);
          setPastReservations(data.pastReservations ?? 0);
          setNotifications(!!data.notifications);
          setColorTheme(data.colorTheme ?? '#1a475f');
          setAresUserText(!!data.aresUserText);
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    fetchSettings();
  }, [selectedLocation]);

  useEffect(() => {
    async function fetchData() {
      try {
        // --- Load GeoJSON ---
        const geojsonResponse = await fetch(getGeojsonFile(selectedLocation));
        if (!geojsonResponse.ok) throw new Error('Failed to load GeoJSON');
        const geojsonData = await geojsonResponse.json();
  
        const countWithName = geojsonData.features.filter(
          feature => feature.properties && feature.properties.name
        ).length;
        setAreaCount(countWithName);
  
        // --- Load Reservations ---
        const reservationsResponse = await fetch(
          `${RESERVATIONS_URL}${selectedLocation}.json`
        );
        if (!reservationsResponse.ok)
          throw new Error('Failed to load reservations');
        const reservationsData = await reservationsResponse.json();
  
        // Assuming reservationsData is an array
        setCurrentReservations(reservationsData.length);
      } catch (error) {
        console.error(error);
      }
    }
  
    fetchData();
  }, [selectedLocation]);  
  
  const handleAddHousekeeper = async () => {
    if (!newHousekeeperCID) return;
    try {
      const response = await fetch(DATA_URL + `housekeepers?t=${Date.now()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify({
          cid: newHousekeeperCID,
          location: selectedLocation
        })
      });
  
      if (response.ok) {
        setHousekeepers(prev => [...prev, newHousekeeperCID]);
        setNewHousekeeperCID('');
      } else {
        const data = await response.json();
        alert(data.error + '. User shall have a profile on V-LARA.' || 'Failed to add housekeeper');
      }
    } catch (err) {
      console.error('Error adding housekeeper:', err);
      alert('Error adding housekeeper');
    }
  };  

  const handleRemoveHousekeeper = async (cid) => {
    const confirmed = window.confirm(`Confirm remove Housekeeper role for ${cid}?`);
    if (!confirmed) return;
  
    try {
      const response = await fetch(DATA_URL + `housekeepers?t=${Date.now()}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify({ cid, location: selectedLocation }) // <-- add location
      });
  
      if (response.ok) {
        setHousekeepers(prev => prev.filter(h => h !== cid));
      } else {
        const data = await response.json();
        console.error('Failed to remove housekeeper:', data.error || response.statusText);
        alert(data.error || 'Failed to remove housekeeper');
      }
    } catch (err) {
      console.error('Error removing housekeeper:', err);
      alert('Error removing housekeeper');
    }
  };  

  const handleReport = async () => {
    try {
      const response = await fetch(DATA_URL + `reportMisuse?t=${Date.now()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify({
          reportingUserCID: userData.cid,
          targetCID: reportCID,
          explanation: reportExplanation
        })
      });
      if (response.ok) {
        alert('Report submitted successfully');
        setReportCID('');
        setReportExplanation('');
      } else {
        alert('Failed to submit report');
      }
    } catch (err) {
      console.error('Error submitting report:', err);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const response = await fetch(DATA_URL + `settings?t=${Date.now()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          location: selectedLocation,
          settings: {
            maxReservableByOne,
            maxReservations,
            maxAreasInOne,
            forceVerticalLimits,
            clusterIsActive,
            notifications,
            colorTheme,
              aresUserText
          }
        })
      });
      if (response.ok) alert('Settings saved!');
      else alert('Failed to save settings');
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Error saving settings');
    }
  };
  
  if (!showClusterManager) return null;

  return (
    <>
      <div className="notification-modal-overlay" onClick={closeClusterManager} />
      <div className="notification-modal" style={{ width: '700px' }}>
        <div className='about-modal'>
          <img src='images/setup.svg' alt='Logo' style={{ backgroundColor: '#9c27b0' }} />
          <h4>{selectedLocation.toUpperCase()} Cluster Housekeeping</h4>
        </div>

        <Box sx={{ mt: 2, mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>

          {/* Statistics Row */}
          <Grid container spacing={4} justifyContent="center" textAlign="center">
            <Grid item xs={4}>
              <Typography variant="h4">{currentReservations}</Typography>
              <Typography variant="body2" color="text.secondary">Current Reservations</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="h4">{pastReservations}</Typography>
              <Typography variant="body2" color="text.secondary">Past Reservations</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="h4">{areaCount}</Typography>
              <Typography variant="body2" color="text.secondary">Reservable Areas</Typography>
            </Grid>
          </Grid>

          {/* User Details */}
          <Typography sx={{ mt: 2, mb: 1}}>Your Details</Typography>     
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 0 }}>
            <Chip label={'ID ' + userData.cid} size="small" />    
            <Chip label="AMC" size="small" sx={{backgroundColor: '#3b86cb', color: 'white'}}/>
            <Chip label="National Housekeeper" color="secondary" size="small" />
          </Box>
          <Typography variant="body2" color="text.secondary">Logged in {userData.last_connected_at}</Typography>

          {/* Housekeepers */}
          <Box sx={{ mt: 2}}>
            <Typography sx={{ mb: 1}}>{selectedLocation.toUpperCase()} National Housekeepers</Typography>    
            <Stack spacing={0}>
              {housekeepers.map((cid) => (
                <Box key={cid} sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between', maxWidth: 160 }}>
                  <Chip label={'ID ' + cid} size="small" />
                  <IconButton color="error" size="small" onClick={() => handleRemoveHousekeeper(cid)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              {/* Add new housekeeper */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <TextField
                  size="small"
                  label="Add Housekeeper"
                  variant="outlined"
                  value={newHousekeeperCID}
                  onChange={(e) => setNewHousekeeperCID(e.target.value)}
                />
                <Button variant="outlined" color='secondary' onClick={handleAddHousekeeper}>
                  Add
                </Button>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Cluster Settings</Typography>

            {[ 
                {
                  label: 'Cluster is Active',
                  value: clusterIsActive,
                  onChange: (v) => setClusterIsActive(v),
                  type: 'switch'
                },
                {
                    label: 'Max Reservations by one user',
                    value: maxReservableByOne,
                    onChange: (v) => setMaxReservableByOne(v),
                    min: 1,
                    max: 10,
                    type: 'slider'
                },
                {
                    label: 'Max number of Areas in one Reservation',
                    value: maxAreasInOne,
                    onChange: (v) => setMaxAreasInOne(v),
                    min: 1,
                    max: 30,
                    type: 'slider'
                },
                {
                  label: 'Max number of Reservations in FIR',
                  value: maxReservations,
                  onChange: (v) => setMaxReservations(v),
                  min: 2,
                  max: 20,
                  type: 'slider'
                },
                {
                  label: 'Apply validation to Vertical Limits',
                  value: forceVerticalLimits,
                  onChange: (v) => setForceVerticalLimits(v),
                  type: 'switch'
                },
                {
                  label: 'Discord Notifications Active',
                  value: notifications,
                  onChange: (v) => setNotifications(v),
                  type: 'switch'
              },
            ].map((row, i) => (
                <Grid container spacing={1} alignItems="center" key={i} sx={{ mb: 1 }}>
                    {/* Column 1: Label */}
                    <Grid item xs={6}>
                        <Chip label={row.label} size="small" sx={{ width: '100%', justifyContent: 'flex-start' }} />
                    </Grid>

                    {/* Column 2: Value */}
                    <Grid item xs={2}>
                        <Chip
                        sx={{ fontWeight: 'bold', width: '100%', justifyContent: 'center' }}
                        label={row.type === 'switch'
                            ? (row.value ? 'True' : 'False')
                            : row.value}
                        size="small"
                        color={row.type === 'switch'
                            ? (row.value ? 'primary' : 'default')
                            : 'primary'}
                        />
                    </Grid>

                    {/* Column 3: Control */}
                    <Grid item xs={4}>
                        {row.type === 'slider' ? (
                        <Slider
                            value={row.value}
                            onChange={(e, newValue) => row.onChange(newValue)}
                            min={row.min}
                            max={row.max}
                        />
                        ) : (
                        <Switch
                            checked={row.value}
                            onChange={(e) => row.onChange(e.target.checked)}
                        />
                        )}
                    </Grid>
                </Grid>
            ))}
            
            <Grid container spacing={1} alignItems="center" sx={{ mb: 2, mt: '-2px'}}>
              <Grid item xs={6}><Chip label="Background Theme (default #1a475f)" size="small" sx={{ width: '100%', justifyContent: 'flex-start' }} /></Grid>
              <Grid item xs={2}>
                <Box sx={{ bgcolor: colorTheme, borderRadius:4, textAlign:'center', height:24, display: 'flex', alignItems: 'center', justifyContent: 'center', }}>
                  <Typography variant="body2" sx={{fontSize:12, color:'#fff', fontWeight:'bold'}}>Preview</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <TextField size="small" fullWidth value={colorTheme} onChange={e=>setColorTheme(e.target.value)} placeholder="#dfebeb" inputProps={{ maxLength: 7 }} sx={{ '& .MuiInputBase-root': { height: 24, fontSize: 12 } }}/>
              </Grid>

            </Grid>


            <Button variant="outlined" color="secondary" onClick={handleSaveSettings}>Save Settings</Button>
          </Box>

          {/* Discord webhook */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Discord Webhook</Typography>
            <Typography variant='body2' sx={{ mb: 2 }}>
              A Discord Webhook may be provided to send notifications of upcoming and active V-LARA airspace reservations to a Discord Server.
              The notification is updated whenever the reservation details change, and deleted when the reservation is cancelled.
            </Typography>
            <Typography variant='body2'>Send the Webhook to Otto Tuhkunen. It will be added to backend envinonmental variables (confidential). Notifications can be activated or deactivated in the Settings above.</Typography>
          </Box>

          {/* Report Misuse */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Report Misuse of V-LARA</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                label="User ID"
                color="error"
                variant="outlined"
                value={reportCID}
                onChange={(e) => setReportCID(e.target.value)}
              />
              <TextField
                size="small"
                label="Explanation"
                variant="outlined"
                color="error"
                fullWidth
                value={reportExplanation}
                onChange={(e) => setReportExplanation(e.target.value)}
              />
              <Button variant="outlined" color="error" onClick={handleReport}>Send</Button>
            </Stack>
          </Box>

        </Box>

        <DialogActions style={{ justifyContent: 'center', gap: 10, marginTop: '20px' }}>
          <Button
            onClick={() => { closeClusterManager(); window.location = 'https://github.com/ottotuhkunen/virtual-lara-airspace-data/'; }}
            variant="contained"
            sx={{ lineHeight: 1.2, minHeight: '36.5px', textAlign: 'left', borderRadius: 0, color: 'white', backgroundColor: 'rgba(0,0,0,.8)', '&:hover': {backgroundColor: 'black'}}}
            startIcon={<GitHubIcon />}
            >
            Data Files
          </Button>
          <Button onClick={() => { closeClusterManager(); window.location.hash = '#/dqt'; }} variant="contained" sx={{ lineHeight: 1.2, minHeight: '36.5px', borderRadius: 0, textAlign: 'left'}} color="secondary">
            <img src='images/dqt.svg' alt='Data Quality Tool' style={{height: '16px', marginRight: '8px'}}></img>Data Quality Tool
          </Button>

          <Button onClick={() => { closeClusterManager(); window.location.hash = '#/converter'; }} variant="contained" sx={{ lineHeight: 1.2, minHeight: '36.5px', borderRadius: 0, textAlign: 'left'}} color="secondary">
            <img src='images/converter.svg' alt='Converter' style={{height: '16px', marginRight: '8px'}}></img>TopSky Converter
          </Button>

        </DialogActions>

        <DialogActions style={{ justifyContent: 'center', gap: 10, marginTop: '10px' }}>
          <Button onClick={closeClusterManager} variant="contained" sx={{ borderRadius: 0 }}>
              Close
          </Button>
        </DialogActions>
        

      </div>
    </>
  );
};

export default ClusterManager;
